/**
 * Farm & Acre Co — Live Tag Visualiser (Stage 1, Round 1.5)
 *
 * Mirrors customer colour + print layout + line text into an inline SVG.
 * Reads from form-visual-tag.liquid hooks and the print-program data block.
 *
 * LAYOUT_CATALOG below is the authoritative source for every Allflex print
 * program: y-positions, font sizes, char limits. Live metaobject data overrides
 * if present; missing entries fall back to catalog so the visualiser stays
 * correct even when metaobject records have gaps (IZ7/IZP/IZR currently).
 */

(function () {
  'use strict';

  // Vibrant Allflex-style cattle tag colours. Hexes were tuned against real
  // product photography — the prior palette read as washed-out.
  const COLOUR_MAP = {
    'black':         { fill: '#1A1A1A', text: '#FFFFFF' },
    'blue':          { fill: '#0099D9', text: '#FFFFFF' },
    'blue-light':    { fill: '#4EC1E0', text: '#111111' },
    'blue-dark':     { fill: '#1057A8', text: '#FFFFFF' },
    'brown':         { fill: '#8B4513', text: '#FFFFFF' },
    'green':         { fill: '#3FAE49', text: '#FFFFFF' },
    'green-light':   { fill: '#A5D63F', text: '#111111' },
    'green-neon':    { fill: '#76B843', text: '#FFFFFF' },
    'green-dark':    { fill: '#1E6F2E', text: '#FFFFFF' },
    'grey':          { fill: '#7A7A7A', text: '#FFFFFF' },
    'gray':          { fill: '#7A7A7A', text: '#FFFFFF' },
    'orange':        { fill: '#FF6F00', text: '#111111' },
    'orange-light':  { fill: '#FF9233', text: '#111111' },
    'orange-dark':   { fill: '#D84A00', text: '#FFFFFF' },
    'pink':          { fill: '#E94B7F', text: '#FFFFFF' },
    'pink-light':    { fill: '#F498B6', text: '#111111' },
    'pink-dark':     { fill: '#B71F58', text: '#FFFFFF' },
    'purple':        { fill: '#7E2C8A', text: '#FFFFFF' },
    'purple-light':  { fill: '#B580C9', text: '#111111' },
    'red':           { fill: '#E32227', text: '#FFFFFF' },
    'red-light':     { fill: '#F25D63', text: '#FFFFFF' },
    'white':         { fill: '#FFFFFF', text: '#111111' },
    'yellow':        { fill: '#FFE100', text: '#111111' },
    'hot-pink':      { fill: '#EC008C', text: '#FFFFFF' },
    'silky-pink':    { fill: '#F4A7C0', text: '#111111' },
    'silky-blue':    { fill: '#7FB9DD', text: '#111111' },
    'grass-green':   { fill: '#5BAA3C', text: '#FFFFFF' }
  };

  // Authoritative Allflex print program catalog, indexed by LAYOUT LETTER.
  // Allflex program IDs are <PREFIX><LETTER>000000 — the prefix encodes tag size
  // (IZ = Super Maxi, IA = Maxi, etc.) but the layout letter is the same across
  // sizes: 1, 6, 7, 9, H, J, B, D, P, R. So IZ7000000 and IA7000000 share the
  // same y_pct positions; only font sizes differ (handled by the live metaobject
  // data merge in mergeSpec).
  //
  // Source: Aaron's catalog screenshots from allflex.com.au tag marking programs.
  // y_pct = fraction of tag body height; font_size values are Super-Maxi defaults
  // used only as a fallback when the per-product metaobject has data gaps.
  const LAYOUT_CATALOG = {
    '1': [
      { line: 1, y_pct: 0.55, font_size: 45, max_chars: 5,  align: 'center', font_type: 'ink' }
    ],
    '6': [
      { line: 1, y_pct: 0.22, font_size: 19, max_chars: 3,  align: 'center', font_type: 'laser' },
      { line: 2, y_pct: 0.62, font_size: 45, max_chars: 5,  align: 'center', font_type: 'ink' }
    ],
    '7': [
      { line: 1, y_pct: 0.16, font_size: 9,  max_chars: 4,  align: 'center', font_type: 'lazer' },
      { line: 2, y_pct: 0.28, font_size: 9,  max_chars: 10, align: 'center', font_type: 'lazer' },
      { line: 3, y_pct: 0.66, font_size: 45, max_chars: 5,  align: 'center', font_type: 'ink' }
    ],
    '9': [
      { line: 1, y_pct: 0.18, font_size: 15, max_chars: 4,  align: 'center', font_type: 'lazer' },
      { line: 2, y_pct: 0.40, font_size: 15, max_chars: 8,  align: 'center', font_type: 'ink' },
      { line: 3, y_pct: 0.74, font_size: 30, max_chars: 6,  align: 'center', font_type: 'ink' }
    ],
    'H': [
      { line: 1, y_pct: 0.18, font_size: 9,  max_chars: 10, align: 'center', font_type: 'laser' },
      { line: 2, y_pct: 0.46, font_size: 30, max_chars: 6,  align: 'center', font_type: 'ink' },
      { line: 3, y_pct: 0.82, font_size: 9,  max_chars: 15, align: 'center', font_type: 'lazer' }
    ],
    'J': [
      { line: 1, y_pct: 0.18, font_size: 12, max_chars: 5,  align: 'center', font_type: 'lazer' },
      { line: 2, y_pct: 0.46, font_size: 30, max_chars: 6,  align: 'center', font_type: 'ink' },
      { line: 3, y_pct: 0.82, font_size: 12, max_chars: 10, align: 'center', font_type: 'ink' }
    ],
    'B': [
      { line: 1, y_pct: 0.14, font_size: 7,  max_chars: 6,  align: 'center', font_type: 'laser' },
      { line: 2, y_pct: 0.26, font_size: 7,  max_chars: 15, align: 'center', font_type: 'laser' },
      { line: 3, y_pct: 0.38, font_size: 7,  max_chars: 15, align: 'center', font_type: 'laser' },
      { line: 4, y_pct: 0.72, font_size: 45, max_chars: 5,  align: 'center', font_type: 'ink' }
    ],
    'D': [
      { line: 1, y_pct: 0.16, font_size: 11, max_chars: 4,  align: 'center', font_type: 'laser' },
      { line: 2, y_pct: 0.32, font_size: 11, max_chars: 9,  align: 'center', font_type: 'laser' },
      { line: 3, y_pct: 0.52, font_size: 12, max_chars: 10, align: 'center', font_type: 'ink' },
      { line: 4, y_pct: 0.80, font_size: 30, max_chars: 6,  align: 'center', font_type: 'ink' }
    ],
    'P': [
      { line: 1, y_pct: 0.18, font_size: 15, max_chars: 4,  align: 'center', font_type: 'laser' },
      { line: 2, y_pct: 0.38, font_size: 15, max_chars: 8,  align: 'center', font_type: 'ink' },
      { line: 3, y_pct: 0.58, font_size: 15, max_chars: 8,  align: 'center', font_type: 'ink' },
      { line: 4, y_pct: 0.78, font_size: 15, max_chars: 8,  align: 'center', font_type: 'ink' }
    ],
    'R': [
      { line: 1, y_pct: 0.16, font_size: 12, max_chars: 4,  align: 'center', font_type: 'lazer' },
      { line: 2, y_pct: 0.32, font_size: 12, max_chars: 7,  align: 'center', font_type: 'lazer' },
      { line: 3, y_pct: 0.50, font_size: 12, max_chars: 10, align: 'center', font_type: 'ink' },
      { line: 4, y_pct: 0.66, font_size: 12, max_chars: 10, align: 'center', font_type: 'ink' },
      { line: 5, y_pct: 0.82, font_size: 12, max_chars: 10, align: 'center', font_type: 'ink' }
    ]
  };

  // FOFIA line-print styles (Style 1/2/3), keyed by the FOFIA Print Style select
  // value. FOFIA tags carry no Allflex print-program metaobjects, so program IDs
  // like "2-lines" don't resolve through layoutKey()/LAYOUT_CATALOG — without this
  // catalog a FOFIA management/maxi tag falls back to generic even spacing + a flat
  // 10-char limit. Char limits per the FOFIA supplier spec: Style 1 = 1 line ≤10;
  // Style 2 = L1 ≤8 / L2 ≤10; Style 3 = L1 ≤6 / L2 ≤10 / L3 ≤10. Font sizes are
  // Super-Maxi-body (80mm) reference values scaled per-tag in mergeSpec. Only used
  // when the wrapper is flagged data-vendor="FOFIA" (see catalogFor / isFofia).
  const FOFIA_LAYOUT_CATALOG = {
    '1-line': [
      { line: 1, y_pct: 0.58, font_size: 44, max_chars: 10, align: 'center', font_type: 'laser' }
    ],
    '2-lines': [
      { line: 1, y_pct: 0.40, font_size: 24, max_chars: 8,  align: 'center', font_type: 'laser' },
      { line: 2, y_pct: 0.76, font_size: 38, max_chars: 10, align: 'center', font_type: 'laser' }
    ],
    '3-lines': [
      { line: 1, y_pct: 0.28, font_size: 16, max_chars: 6,  align: 'center', font_type: 'laser' },
      { line: 2, y_pct: 0.52, font_size: 20, max_chars: 10, align: 'center', font_type: 'laser' },
      { line: 3, y_pct: 0.80, font_size: 30, max_chars: 10, align: 'center', font_type: 'laser' }
    ],
    // Round-2 FOFIA styles 1-4 keyed by the descriptive select VALUES from
    // form-visual-tag.liquid (the value doubles as the packing-slip spec, so the
    // key must match it character-for-character). Proportions mirror the official
    // FOFIA layouts: the y positions and font sizes make the preview visibly show
    // each line's share of the print height, and `pct` feeds the per-line height
    // hint next to the Line N inputs (syncInputAttrs). The three legacy keys above
    // stay for FOFIA products still using "1-line"/"2-lines"/"3-lines" values.
    'Style 1 (1 line: 100%)': [
      { line: 1, y_pct: 0.58, font_size: 44, max_chars: 10, align: 'center', font_type: 'laser', pct: '100%' }
    ],
    'Style 2 (2 lines: 20% / 80%)': [
      { line: 1, y_pct: 0.28, font_size: 15, max_chars: 8,  align: 'center', font_type: 'laser', pct: '20%' },
      { line: 2, y_pct: 0.72, font_size: 40, max_chars: 10, align: 'center', font_type: 'laser', pct: '80%' }
    ],
    'Style 3 (3 lines: 20% / 60% / 20%)': [
      { line: 1, y_pct: 0.22, font_size: 14, max_chars: 6,  align: 'center', font_type: 'laser', pct: '20%' },
      { line: 2, y_pct: 0.56, font_size: 34, max_chars: 10, align: 'center', font_type: 'laser', pct: '60%' },
      { line: 3, y_pct: 0.85, font_size: 13, max_chars: 10, align: 'center', font_type: 'laser', pct: '20%' }
    ],
    'Style 4 (3 lines: 20% / 20% / 60%)': [
      { line: 1, y_pct: 0.20, font_size: 14, max_chars: 6,  align: 'center', font_type: 'laser', pct: '20%' },
      { line: 2, y_pct: 0.37, font_size: 13, max_chars: 10, align: 'center', font_type: 'laser', pct: '20%' },
      { line: 3, y_pct: 0.75, font_size: 34, max_chars: 10, align: 'center', font_type: 'laser', pct: '60%' }
    ]
  };

  // Extract the layout letter from an Allflex program ID. Program IDs are formatted
  // <PREFIX><LETTER>0000+ but the prefix length varies by tag size:
  //   Super Maxi : IZ1000000  → prefix "IZ" (2 chars) + letter "1"
  //   Maxi       : IA1000000  → prefix "IA" (2 chars) + letter "1"
  //   Medium Male: I00M10000  → prefix "I00M" (4 chars) + letter "1"
  //   Medium Male: 00MM0000   → prefix "00M" (3 chars) + letter "M"
  // We extract the last single non-zero alphanumeric character that precedes
  // the trailing run of zeros, which is the layout letter across all formats.
  function layoutKey(programId) {
    if (!programId || typeof programId !== 'string') return null;
    const m = programId.match(/([1-9A-Z])0+$/);
    return m ? m[1] : null;
  }

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const PLACEHOLDER_PER_LINE = { 1: 'PIC', 2: '12345', 3: 'TAG NAME', 4: 'EXTRA', 5: 'EXTRA' };
  // Arial Black average glyph width as a fraction of em. Used by every render
  // mode to predict text overflow and apply textLength only when needed —
  // prevents the "10 chars overflow but 11 chars get squished" inconsistency.
  const TEXT_AVG_RATIO = 0.62;
  function shouldClampWidth(text, fontSize, maxWidth) {
    if (!text || text.length <= 1) return false;
    return (text.length * fontSize * TEXT_AVG_RATIO) > maxWidth;
  }

  function colourFromName(name) {
    if (!name) return COLOUR_MAP['white'];
    // Sheep/goat year-colour swatches use values like "Orange (Year 2026)" or
    // "Pink (Post Breeder)". Strip the parenthetical so the swatch hex maps.
    const baseName = String(name).split(' (')[0].trim();
    const slug = baseName.toLowerCase().replace(/\s+/g, '-');
    return COLOUR_MAP[slug] || COLOUR_MAP['white'];
  }

  // Super Maxi reference body height (80mm). Used to scale catalog font sizes
  // when rendering on smaller tags. The catalog hex constants are calibrated for
  // Super Maxi; for a Medium tag with ~56mm body the scale is 56/80 = 0.7.
  const REF_BODY_HEIGHT = 80;

  function mergeSpec(catalogSpec, liveSpec, bodyHeight) {
    // Live metaobject data wins when present. When live data is missing (Medium
    // Male `00MM0000` / `00MR0000` / `I00M60000` data gaps), the catalog values
    // are used — but the catalog is calibrated for Super Maxi, so scale by body.
    const scale = bodyHeight ? (bodyHeight / REF_BODY_HEIGHT) : 1;
    const liveFont = liveSpec && liveSpec.font_size;
    const liveMax  = liveSpec && liveSpec.max_chars;
    return {
      line: catalogSpec.line,
      y_pct: catalogSpec.y_pct,
      align: catalogSpec.align,
      font_size: liveFont || (catalogSpec.font_size && catalogSpec.font_size * scale),
      max_chars: liveMax  || catalogSpec.max_chars,
      font_type: (liveSpec && liveSpec.font_type) || catalogSpec.font_type
    };
  }

  function syncInputAttrs(programId, layout, liveLines) {
    // Live metaobject data wins for per-line max_chars + font_size, so the input
    // maxLength reflects the product's actual print-program spec (5 chars for
    // Medium Male I00MB0000 line 1) rather than the Super-Maxi catalog default.
    const live = liveLines || [];
    layout.forEach(spec => {
      const liveSpec = live.find(l => l.line === spec.line);
      const maxChars = (liveSpec && liveSpec.max_chars) || spec.max_chars;
      const fontSize = (liveSpec && liveSpec.font_size) || spec.font_size;
      const fontType = (liveSpec && liveSpec.font_type) || spec.font_type;
      const input = document.querySelector(
        'input[data-allflex-field="print-line"][data-line="' + spec.line + '"]'
      );
      if (!input) return;
      if (input.maxLength !== maxChars) {
        input.maxLength = maxChars;
        input.placeholder = 'Max ' + maxChars + ' characters';
        // Clip already-typed text that exceeds the new limit
        if (input.value.length > maxChars) input.value = input.value.slice(0, maxChars);
      }
      const lineEl = input.closest('.print-line-input');
      const counter = lineEl && lineEl.querySelector('.print-line-char-counter');
      if (counter) {
        counter.textContent = '(' + input.value.length + '/' + maxChars + ')';
      }
      const label = lineEl && lineEl.querySelector('.allflex-field__label');
      const specSpan = label && label.querySelector('.print-line-spec');
      if (specSpan && fontSize && fontType) {
        specSpan.textContent = '(' + fontSize + 'mm ' + fontType + ')';
      }
      // FOFIA styles carry a per-line height share (pct) — surface it next to the
      // line label ("Line 2 · 60% of tag height") so customers instantly see which
      // input is the big line. Allflex catalog specs have no pct, so this clears.
      const pctSpan = label && label.querySelector('[data-line-pct-hint]');
      if (pctSpan) {
        pctSpan.textContent = spec.pct ? '· ' + spec.pct + ' of tag height' : '';
      }
    });
  }

  // Fallback y-positions when a print program isn't in LAYOUT_CATALOG
  // (used by Mini/Button or any product with non-catalog program IDs).
  // Distributes lines evenly inside body height.
  const FALLBACK_Y_PCT = {
    1: [0.55],
    2: [0.30, 0.72],
    3: [0.22, 0.50, 0.78],
    4: [0.18, 0.40, 0.62, 0.84],
    5: [0.14, 0.32, 0.50, 0.68, 0.86]
  };

  function initVisualiser(root) {
    const svg = root.querySelector('[data-tv-shape]');
    if (!svg) return;

    const renderMode = svg.dataset.tvRenderMode || 'cattle';
    if (renderMode === 'feedlot-twoface') return initFeedlotVisualiser(root, svg);
    if (renderMode === 'sheep-nlis')      return initSheepVisualiser(root, svg);
    if (renderMode === 'combo-twoface')   return initComboVisualiser(root, svg);

    // FOFIA line-print tags have no Allflex print-program metaobjects, so their
    // synthetic program IDs ("1-line"/"2-lines"/"3-lines") don't resolve through
    // LAYOUT_CATALOG. When the wrapper is flagged data-vendor="FOFIA", route layout
    // lookups through FOFIA_LAYOUT_CATALOG instead. Allflex products never set this.
    const isFofia = (root.dataset.vendor || '') === 'FOFIA';
    function catalogFor(program) {
      const pid = program && program.program_id;
      if (!pid) return null;
      if (isFofia && FOFIA_LAYOUT_CATALOG[pid]) return FOFIA_LAYOUT_CATALOG[pid];
      return LAYOUT_CATALOG[layoutKey(pid)] || null;
    }

    const bodyY      = parseFloat(svg.dataset.tvBodyY)      || 0;
    const bodyX      = parseFloat(svg.dataset.tvBodyX)      || 0;
    const bodyHeight = parseFloat(svg.dataset.tvBodyHeight) || 100;
    const bodyWidth  = parseFloat(svg.dataset.tvBodyWidth)  || 80;
    const fillTarget   = svg.querySelector('[data-tv-fill]');
    const linesGroup   = svg.querySelector('[data-tv-lines]');
    const unavailable  = root.querySelector('[data-tv-unavailable]');
    const caption      = root.querySelector('[data-tv-caption]');

    let currentProgram = null;
    let currentColour  = colourFromName('white');
    let pendingFrame   = null;

    function clearLines() {
      while (linesGroup.firstChild) linesGroup.removeChild(linesGroup.firstChild);
    }

    function setUnavailable() {
      root.dataset.tvState = 'unavailable';
      if (unavailable) unavailable.hidden = false;
      if (caption) caption.hidden = true;
      clearLines();
    }

    function setBlank() {
      root.dataset.tvState = 'blank';
      if (unavailable) unavailable.hidden = true;
      if (caption) caption.hidden = false;
      clearLines();
    }

    function setAvailable() {
      delete root.dataset.tvState;
      if (unavailable) unavailable.hidden = true;
      if (caption) caption.hidden = false;
    }

    function readLineInputs() {
      const inputs = document.querySelectorAll('input[data-allflex-field="print-line"]');
      const values = {};
      inputs.forEach(i => {
        const n = parseInt(i.dataset.line, 10);
        if (n) values[n] = i.value || '';
      });
      return values;
    }

    function buildLayoutSpecs() {
      // Returns an array of per-line layout specs for the current program.
      // Prefers LAYOUT_CATALOG (keyed by Allflex layout letter — shared across
      // all tag-size prefixes); falls back to inferred positions + a tag-sized
      // default font when the program isn't in the catalog (Mini/Button or
      // products lacking print_program metaobjects).
      const catalogLayout = catalogFor(currentProgram);
      if (catalogLayout) return catalogLayout;

      const liveLines = (currentProgram && currentProgram.lines) || [];
      const lineCount = (currentProgram && currentProgram.line_count) || liveLines.length;
      const yArr = FALLBACK_Y_PCT[lineCount] || FALLBACK_Y_PCT[Math.min(5, Math.max(1, lineCount))];
      if (!yArr) return null;
      // Default font: ~85% of available per-line height, capped to keep it from
      // overflowing on tall narrow tags. Used when the product has no metaobject.
      const defaultFontSize = Math.max(4, Math.min(bodyHeight * 0.85 / Math.max(1, lineCount), bodyHeight * 0.6));
      return yArr.map((y_pct, idx) => ({
        line: idx + 1,
        y_pct: y_pct,
        align: 'center',
        font_size: defaultFontSize,
        max_chars: 10
      }));
    }

    function renderLines() {
      clearLines();
      if (!currentProgram || !currentProgram.program_id) return;

      const layout = buildLayoutSpecs();
      if (!layout) return;

      const values = readLineInputs();

      layout.forEach(catalogSpec => {
        const liveSpec = (currentProgram.lines || []).find(l => l.line === catalogSpec.line);
        const spec = mergeSpec(catalogSpec, liveSpec, bodyHeight);
        if (!spec.font_size || !spec.max_chars) return;  // no data → skip line

        const rawText = (values[spec.line] || '').toUpperCase().slice(0, spec.max_chars);
        const text = rawText || PLACEHOLDER_PER_LINE[spec.line] || '';
        const isPlaceholder = !rawText;

        const y = bodyY + (spec.y_pct * bodyHeight);
        const x = spec.align === 'left'  ? bodyX + 4
                : spec.align === 'right' ? bodyX + bodyWidth - 4
                : bodyX + (bodyWidth / 2);
        const anchor = spec.align === 'left'  ? 'start'
                     : spec.align === 'right' ? 'end'
                     : 'middle';
        const maxTextWidth = bodyWidth - 8;

        const t = document.createElementNS(SVG_NS, 'text');
        t.setAttribute('x', x);
        t.setAttribute('y', y);
        t.setAttribute('font-size', spec.font_size);
        t.setAttribute('text-anchor', anchor);
        t.setAttribute('fill', currentColour.text);
        if (isPlaceholder) t.setAttribute('opacity', '0.22');
        // Squish via textLength only when the natural rendered width would
        // actually overflow the body. Measured estimate (see shouldClampWidth)
        // replaces the brittle char-count threshold that let long PIC-style
        // text bleed past the body before the clamp kicked in.
        if (shouldClampWidth(text, spec.font_size, maxTextWidth)) {
          t.setAttribute('textLength', maxTextWidth);
          t.setAttribute('lengthAdjust', 'spacingAndGlyphs');
        }
        t.textContent = text;
        linesGroup.appendChild(t);
      });
    }

    function applyColour() {
      if (fillTarget) fillTarget.setAttribute('fill', currentColour.fill);
      renderLines();
    }

    function schedule() {
      if (pendingFrame) return;
      pendingFrame = requestAnimationFrame(() => {
        pendingFrame = null;
        renderLines();
        const liveLayout = catalogFor(currentProgram);
        if (liveLayout) {
          // Keep counters in sync as the user types
          liveLayout.forEach(spec => {
            const input = document.querySelector(
              'input[data-allflex-field="print-line"][data-line="' + spec.line + '"]'
            );
            if (!input) return;
            const lineEl = input.closest('.print-line-input');
            const counter = lineEl && lineEl.querySelector('.print-line-char-counter');
            if (counter) counter.textContent = '(' + input.value.length + '/' + spec.max_chars + ')';
          });
        }
      });
    }

    function handleProgramChange(detail) {
      const program = detail && detail.program;
      const lineCount = detail && detail.lineCount;

      // Use the select element's CURRENT VALUE (not the fee tier in detail.selection)
      // to detect real "Blank" or "Custom Layout" choices. Fee tier `"custom"` also
      // fires for any 4/5-line program, which used to false-positive into unavailable.
      const selectEl = document.querySelector('[data-allflex-field="print-style"]');
      const selValue = selectEl ? selectEl.value : null;

      if (selValue === 'blank' || lineCount === 0) {
        currentProgram = null;
        setBlank();
        return;
      }

      if (selValue === 'custom') {
        currentProgram = program;
        setUnavailable();
        return;
      }

      // Real program OR synthetic line-count-only (when product has no metaobject —
      // e.g. Mini Female falls back to "1-line"/"2-lines"/"3-lines" select values).
      currentProgram = program || {
        program_id: selValue || ('lines-' + lineCount),
        lines: [],
        line_count: lineCount
      };
      setAvailable();
      const catalogLayout = catalogFor(currentProgram);
      if (catalogLayout) syncInputAttrs(currentProgram.program_id, catalogLayout, currentProgram.lines);
      renderLines();
    }

    function handleColourChange(e) {
      const radio = e.target.closest('input[type="radio"][data-allflex-field="color"]');
      if (!radio || !radio.checked) return;
      currentColour = colourFromName(radio.value);
      applyColour();
    }

    function handleLineInput(e) {
      const input = e.target.closest('input[data-allflex-field="print-line"]');
      if (!input) return;
      schedule();
    }

    document.addEventListener('printLinesChanged', e => {
      if (!e.detail) return;
      // Accept single-side cattle forms (visual_tag) and female-side dual forms
      // (nlis_cattle matching combos). The female side is always the visual tag
      // (Maxi/Super Maxi/Large Female face); the male side is a Button which has
      // its own Laza printing and is not previewed in Round 2.7.
      const side = e.detail.side;
      if (side && side !== 'single' && side !== 'female') return;
      handleProgramChange(e.detail);
    });

    document.addEventListener('change', handleColourChange);
    document.addEventListener('input', handleLineInput);

    const checkedColour = document.querySelector('input[type="radio"][data-allflex-field="color"]:checked');
    if (checkedColour) currentColour = colourFromName(checkedColour.value);
    applyColour();

    if (window.printLineInputs && typeof window.printLineInputs.getSelectedPrograms === 'function') {
      const sel = window.printLineInputs.getSelectedPrograms();
      if (sel && sel.single) {
        handleProgramChange({
          program: sel.single,
          lineCount: sel.single.line_count,
          side: 'single'
        });
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Feedlot mode — one-piece tags with 1 Side / 2 Sides print + side-by-side
  // FRONT and BACK face preview. Round 2.7: reads structured front/back line
  // inputs (3 + 3) capped at 12 chars each. Empty lines are skipped at render;
  // effective line count drives the layout (1 = centred big, 2/3 = distributed).
  // ---------------------------------------------------------------------------
  function initFeedlotVisualiser(root, svg) {
    const faceFront = svg.querySelector('[data-tv-face="front"]');
    const faceBack  = svg.querySelector('[data-tv-face="back"]');
    const fillFront = svg.querySelector('[data-tv-fill="front"]');
    const fillBack  = svg.querySelector('[data-tv-fill="back"]');
    const linesFront = svg.querySelector('[data-tv-lines="front"]');
    const linesBack  = svg.querySelector('[data-tv-lines="back"]');
    const backRowsContainer = document.querySelector('#feedlot-back-rows');
    const caption = root.querySelector('[data-tv-caption]');

    const FACE_BODY_W = 76, FACE_BODY_H = 80, FACE_BODY_Y = 14;
    const FRONT_CX = 42, BACK_CX = 142;

    let currentColour = colourFromName('white');
    let pendingFrame = null;

    function readSideLines(side) {
      // SIDES-BRANCH (feedlot one-piece print_option_type="sides"): structured
      // feedlot-line inputs with data-side="front"/"back" — up to 3 lines/side.
      // LINES-BRANCH (A-Tag Maxi/Large with print_option_type="lines"): standard
      // print-line inputs with data-line 1..5, no side qualifier. They render
      // as the front face only (lines-branch products have no concept of back).
      const sidesBranchInputs = document.querySelectorAll(
        'input[data-allflex-field="feedlot-line"][data-side="' + side + '"]'
      );
      const result = [];

      // Lines-branch fallback (only relevant for front face)
      if (sidesBranchInputs.length === 0) {
        if (side !== 'front') return result;
        for (let i = 1; i <= 5; i++) {
          const input = document.querySelector(
            'input[data-allflex-field="print-line"][data-line="' + i + '"]'
          );
          if (!input) continue;
          const lineEl = input.closest('.print-line-input');
          const isVisible = lineEl ? lineEl.style.display !== 'none' : true;
          if (!isVisible) continue;
          const v = (input.value || '').trim();
          if (v.length > 0) result.push(v.toUpperCase());
        }
        return result;
      }

      // Sides-branch (existing logic)
      for (let i = 1; i <= 3; i++) {
        const input = document.querySelector(
          'input[data-allflex-field="feedlot-line"][data-side="' + side + '"][data-line="' + i + '"]'
        );
        const v = input ? (input.value || '').trim() : '';
        if (v.length > 0) result.push(v.toUpperCase());
      }
      return result;
    }

    function renderLines(linesGroup, lines, centreX) {
      if (!linesGroup) return;
      while (linesGroup.firstChild) linesGroup.removeChild(linesGroup.firstChild);
      const count = lines.length;
      if (count === 0) return;
      const yArr = FALLBACK_Y_PCT[count] || FALLBACK_Y_PCT[Math.min(5, Math.max(1, count))];
      const fontSize = Math.max(5, Math.min(FACE_BODY_H * 0.85 / count, FACE_BODY_H * 0.6));
      const maxTextWidth = FACE_BODY_W - 8;
      lines.forEach((line, i) => {
        const t = document.createElementNS(SVG_NS, 'text');
        t.setAttribute('x', centreX);
        t.setAttribute('y', FACE_BODY_Y + (yArr[i] * FACE_BODY_H));
        t.setAttribute('font-size', fontSize);
        t.setAttribute('text-anchor', 'middle');
        t.setAttribute('fill', currentColour.text);
        // Measured-width clamp (shared helper) — squish only when natural
        // rendered width would actually overflow the face body. The Round 2.7
        // char-count threshold fired too late: 10 chars at 34mm font overflows
        // the 76mm body well before the 11-char cutoff, so text bled onto the
        // adjacent face.
        if (shouldClampWidth(line, fontSize, maxTextWidth)) {
          t.setAttribute('textLength', maxTextWidth);
          t.setAttribute('lengthAdjust', 'spacingAndGlyphs');
        }
        t.textContent = line;
        linesGroup.appendChild(t);
      });
    }

    function applyColour() {
      if (fillFront) fillFront.setAttribute('fill', currentColour.fill);
      if (fillBack)  fillBack.setAttribute('fill',  currentColour.fill);
      renderAll();
    }

    function renderAll() {
      const printOptSelect = document.querySelector('[data-allflex-field="print-sides"]');
      const printStyleSelect = document.querySelector('[data-allflex-field="print-style"]');
      const frontLines = readSideLines('front');
      const backLines  = readSideLines('back');

      // LINES-BRANCH path (A-Tag Maxi/Large etc.): no print-sides select, has
      // print-style instead. Always render single front face — these are
      // one-piece tags with no concept of "back" in the form data model.
      if (!printOptSelect && printStyleSelect) {
        faceFront.style.display = '';
        faceBack.style.display = 'none';
        if (backRowsContainer) backRowsContainer.style.display = 'none';
        const styleValue = printStyleSelect.value;
        if (!styleValue || styleValue === 'blank') {
          renderLines(linesFront, [], FRONT_CX);
          if (caption) caption.textContent = 'Live preview — blank tag, no print';
        } else {
          renderLines(linesFront, frontLines, FRONT_CX);
          if (caption) caption.textContent = frontLines.length > 0
            ? 'Live preview — front side print'
            : 'Live preview — type your text below';
        }
        return;
      }

      const sidesValue = printOptSelect ? printOptSelect.value : '';
      if (sidesValue === 'No Print' || !sidesValue) {
        faceFront.style.display = '';
        faceBack.style.display = 'none';
        renderLines(linesFront, [], FRONT_CX);
        renderLines(linesBack, [], BACK_CX);
        if (backRowsContainer) backRowsContainer.style.display = 'none';
        if (caption) caption.textContent = 'Live preview — blank tag, no print';
        return;
      }
      if (sidesValue === 'Printed 1 Side') {
        faceFront.style.display = '';
        faceBack.style.display = 'none';
        renderLines(linesFront, frontLines, FRONT_CX);
        renderLines(linesBack, [], BACK_CX);
        if (backRowsContainer) backRowsContainer.style.display = 'none';
        if (caption) caption.textContent = frontLines.length > 0
          ? 'Live preview — printed one side'
          : 'Live preview — type your text below';
        return;
      }
      // Printed 2 Sides — render only what's actually typed on each side. No
      // placeholder text on either face (caused the "FRONT TEXT" / "RT" bug).
      faceFront.style.display = '';
      faceBack.style.display = '';
      if (backRowsContainer) backRowsContainer.style.display = '';
      renderLines(linesFront, frontLines, FRONT_CX);
      renderLines(linesBack,  backLines,  BACK_CX);
      if (caption) caption.textContent = (frontLines.length + backLines.length) > 0
        ? 'Live preview — printed both sides'
        : 'Live preview — type your text below';
    }

    function schedule() {
      if (pendingFrame) return;
      pendingFrame = requestAnimationFrame(() => { pendingFrame = null; renderAll(); });
    }

    document.addEventListener('change', e => {
      const radio = e.target.closest('input[type="radio"][data-allflex-field="color"]');
      if (radio && radio.checked) {
        currentColour = colourFromName(radio.value);
        applyColour();
        return;
      }
      // Both sides-branch (print-sides) and lines-branch (print-style) selects
      // trigger a re-render. Lines-branch fires for the A-Tag Maxi/Large
      // family which use the standard cattle "lines" form.
      const trigger = e.target.closest('[data-allflex-field="print-sides"], [data-allflex-field="print-style"]');
      if (trigger) schedule();
    });
    document.addEventListener('input', e => {
      // Listen to BOTH input types: feedlot-line (sides-branch redesign) and
      // print-line (lines-branch products routed to feedlot SVG, e.g. A-Tag
      // Maxi which has tag_shape_handle=feedlot-onepiece but uses print_option_type="lines").
      if (e.target.closest('input[data-allflex-field="feedlot-line"], input[data-allflex-field="print-line"]')) {
        schedule();
      }
    });
    // print-line-inputs.js fires printLinesChanged on the single-side cattle
    // form; trigger a re-render so lines-branch feedlot products update when
    // the print-style select changes (the change handler above also catches it,
    // but printLinesChanged is more reliable for initial-state sync).
    document.addEventListener('printLinesChanged', e => {
      if (e.detail && e.detail.side === 'single') schedule();
    });

    const checkedColour = document.querySelector('input[type="radio"][data-allflex-field="color"]:checked');
    if (checkedColour) currentColour = colourFromName(checkedColour.value);
    applyColour();
  }

  // ---------------------------------------------------------------------------
  // Sheep NLIS mode — long horizontal tag with male end (left) and female end
  // (right). Six print options (Option 1..6) define what goes on each side.
  // Tag Side select swaps left/right content. Health Symbol overlays as a
  // small badge on the female side.
  // ---------------------------------------------------------------------------
  const SHEEP_OPTIONS = {
    'Option 1': {
      male:   [{ y_pct: 0.32, source: 'literal', text: 'DO NOT' },
               { y_pct: 0.68, source: 'literal', text: 'REMOVE' }],
      female: [{ y_pct: 0.32, source: 'nlis-line1' },
               { y_pct: 0.68, source: 'nlis-line2' }]
    },
    'Option 2': {
      male:   [{ y_pct: 0.55, source: 'start-number', big: true }],
      female: [{ y_pct: 0.32, source: 'nlis-line1' },
               { y_pct: 0.68, source: 'nlis-line2' }]
    },
    'Option 3': {
      male:   [{ y_pct: 0.55, source: 'property-name' }],
      female: [{ y_pct: 0.32, source: 'nlis-line1' },
               { y_pct: 0.68, source: 'nlis-line2' }]
    },
    'Option 4': {
      male:   [{ y_pct: 0.32, source: 'nlis-line1' },
               { y_pct: 0.68, source: 'nlis-line2' }],
      female: [{ y_pct: 0.32, source: 'property-name' },
               { y_pct: 0.68, source: 'start-number' }]
    },
    'Option 5': {
      male:   [{ y_pct: 0.32, source: 'property-name' },
               { y_pct: 0.68, source: 'start-number' }],
      female: [{ y_pct: 0.32, source: 'nlis-line1' },
               { y_pct: 0.68, source: 'nlis-line2' }]
    },
    'Option 6': {
      male:   [{ y_pct: 0.55, source: 'start-number', big: true }],
      female: [{ y_pct: 0.32, source: 'nlis-line1' },
               { y_pct: 0.68, source: 'nlis-line2' }]
    }
  };

  function initSheepVisualiser(root, svg) {
    const fillMale   = svg.querySelector('[data-tv-fill="male"]');
    const fillFemale = svg.querySelector('[data-tv-fill="female"]');
    const linesMale   = svg.querySelector('[data-tv-lines="male"]');
    const linesFemale = svg.querySelector('[data-tv-lines="female"]');
    const symbolSlot  = svg.querySelector('[data-tv-symbol]');
    const caption = root.querySelector('[data-tv-caption]');

    const MALE_BODY_W   = parseFloat(svg.dataset.tvMaleBodyW)   || 70;
    const MALE_BODY_H   = parseFloat(svg.dataset.tvMaleBodyH)   || 28;
    const MALE_BODY_X   = parseFloat(svg.dataset.tvMaleBodyX)   || 12;
    const MALE_BODY_Y   = parseFloat(svg.dataset.tvMaleBodyY)   || 6;
    const FEMALE_BODY_W = parseFloat(svg.dataset.tvFemaleBodyW) || 70;
    const FEMALE_BODY_H = parseFloat(svg.dataset.tvFemaleBodyH) || 28;
    const FEMALE_BODY_X = parseFloat(svg.dataset.tvFemaleBodyX) || 92;
    const FEMALE_BODY_Y = parseFloat(svg.dataset.tvFemaleBodyY) || 6;

    let currentColour = colourFromName('white');
    let pendingFrame = null;

    function readField(id) {
      const el = document.getElementById(id);
      return el ? (el.value || '').trim() : '';
    }

    function sourceToText(source, literal) {
      if (source === 'literal') return literal || '';
      if (source === 'property-name') return (readField('allflex-property') || 'NAME HERE').toUpperCase();
      if (source === 'start-number') {
        const n = readField('allflex-starting-number');
        if (!n) return '00001';
        const digits = n.replace(/[^0-9]/g, '');
        return digits ? digits.padStart(5, '0') : n.toUpperCase();
      }
      if (source === 'nlis-line1') {
        // Approximate NLIS device serial — real value assigned at allocation
        const pic = readField('allflex-pic') || 'NE760009';
        return pic.toUpperCase();
      }
      if (source === 'nlis-line2') {
        const start = readField('allflex-starting-number') || '00001';
        const digits = start.replace(/[^0-9]/g, '');
        const padded = digits ? digits.padStart(5, '0') : start;
        return ('LKS' + padded).toUpperCase();
      }
      return '';
    }

    function renderHalf(linesGroup, lineSpecs, bodyX, bodyY, bodyW, bodyH) {
      while (linesGroup.firstChild) linesGroup.removeChild(linesGroup.firstChild);
      lineSpecs.forEach(spec => {
        const text = sourceToText(spec.source, spec.text);
        if (!text) return;
        const isBig = !!spec.big;
        const fontSize = isBig
          ? bodyH * 0.65
          : Math.max(4, Math.min(bodyH * 0.42, bodyH * 0.45));
        const y = bodyY + (spec.y_pct * bodyH);
        const x = bodyX + bodyW / 2;
        const maxTextWidth = bodyW - 6;

        const t = document.createElementNS(SVG_NS, 'text');
        t.setAttribute('x', x);
        t.setAttribute('y', y);
        t.setAttribute('font-size', fontSize);
        t.setAttribute('text-anchor', 'middle');
        t.setAttribute('fill', currentColour.text);
        // Measured-width clamp (shared helper) — sheep tags have varied limits
        if (shouldClampWidth(text, fontSize, maxTextWidth)) {
          t.setAttribute('textLength', maxTextWidth);
          t.setAttribute('lengthAdjust', 'spacingAndGlyphs');
        }
        t.textContent = text;
        linesGroup.appendChild(t);
      });
    }

    function renderAll() {
      const printOpt = document.getElementById('allflex-print-layout');
      const tagSide  = document.getElementById('allflex-tag-side');
      const symbol   = document.getElementById('allflex-symbol');

      const opt = printOpt ? printOpt.value : '';
      // 2026-06-12: the side control is now "Print Arrangement" (As pictured / Reversed…);
      // legacy "Female side" value kept so old sessions/themes still swap correctly.
      const sideSwap = !!(tagSide && (tagSide.value.indexOf('Reversed') === 0 || tagSide.value === 'Female side'));

      // Print Option is optional on the form — customers often fill out the
      // PIC + Property Name + Start Number first and expect to see them on the
      // visualiser. Default to Option 5 (property-name + start-number on male,
      // NLIS on female) so the visualiser is always informative.
      const spec = SHEEP_OPTIONS[opt] || SHEEP_OPTIONS['Option 5'];
      if (!spec) {
        renderHalf(linesMale,   [{ y_pct: 0.55, source: 'literal', text: 'SELECT OPTION' }],
                   MALE_BODY_X, MALE_BODY_Y, MALE_BODY_W, MALE_BODY_H);
        renderHalf(linesFemale, [{ y_pct: 0.32, source: 'nlis-line1' },
                                  { y_pct: 0.68, source: 'nlis-line2' }],
                   FEMALE_BODY_X, FEMALE_BODY_Y, FEMALE_BODY_W, FEMALE_BODY_H);
      } else {
        const maleSpec   = sideSwap ? spec.female : spec.male;
        const femaleSpec = sideSwap ? spec.male   : spec.female;
        renderHalf(linesMale,   maleSpec,   MALE_BODY_X,   MALE_BODY_Y,   MALE_BODY_W,   MALE_BODY_H);
        renderHalf(linesFemale, femaleSpec, FEMALE_BODY_X, FEMALE_BODY_Y, FEMALE_BODY_W, FEMALE_BODY_H);
      }

      // Health symbol overlay
      if (symbolSlot) {
        const code = symbol ? symbol.value : '';
        if (code && code !== 'No Symbol') {
          const letter = code.split(' ')[0];
          symbolSlot.style.display = '';
          const txt = symbolSlot.querySelector('text');
          if (txt) txt.textContent = letter;
        } else {
          symbolSlot.style.display = 'none';
        }
      }

      if (caption) {
        caption.textContent = opt
          ? 'Live preview — ' + opt + (sideSwap ? ' (reversed)' : '')
          : 'Live preview — default layout (Option 5). Pick a different Print Option to change.';
      }
    }

    function applyColour() {
      if (fillMale)   fillMale.setAttribute('fill',   currentColour.fill);
      if (fillFemale) fillFemale.setAttribute('fill', currentColour.fill);
      renderAll();
    }

    function schedule() {
      if (pendingFrame) return;
      pendingFrame = requestAnimationFrame(() => { pendingFrame = null; renderAll(); });
    }

    document.addEventListener('change', e => {
      const radio = e.target.closest('input[type="radio"][data-allflex-field="color"]');
      if (radio && radio.checked) {
        currentColour = colourFromName(radio.value);
        applyColour();
        return;
      }
      if (e.target.closest('#allflex-print-layout, #allflex-tag-side, #allflex-symbol')) schedule();
    });
    document.addEventListener('input', e => {
      if (e.target.closest('#allflex-pic, #allflex-property, #allflex-starting-number, #allflex-owner')) schedule();
    });

    const checkedColour = document.querySelector('input[type="radio"][data-allflex-field="color"]:checked');
    if (checkedColour) currentColour = colourFromName(checkedColour.value);
    applyColour();
  }

  // ---------------------------------------------------------------------------
  // Combo mode — matching cattle combo / TSU kit products with both a female
  // visual tag AND a male tag (Button or full-size). Renders TWO faces side by
  // side, each driven by its own Print Style + Line N inputs. Customers see
  // both halves update live so they understand what's printed where.
  // ---------------------------------------------------------------------------
  function initComboVisualiser(root, svg) {
    const fillFemale = svg.querySelector('[data-tv-fill="female"]');
    const fillMale   = svg.querySelector('[data-tv-fill="male"]');
    const linesFemale = svg.querySelector('[data-tv-lines="female"]');
    const linesMale   = svg.querySelector('[data-tv-lines="male"]');
    const caption     = root.querySelector('[data-tv-caption]');

    // Female face geometry (rectangle on the left)
    const F_X = parseFloat(svg.dataset.tvFemaleBodyX) || 4;
    const F_Y = parseFloat(svg.dataset.tvFemaleBodyY) || 20;
    const F_W = parseFloat(svg.dataset.tvFemaleBodyW) || 80;
    const F_H = parseFloat(svg.dataset.tvFemaleBodyH) || 80;
    // Male face geometry (button circle on the right)
    const M_CX = parseFloat(svg.dataset.tvMaleCx) || 120;
    const M_CY = parseFloat(svg.dataset.tvMaleCy) || 55;
    const M_R  = parseFloat(svg.dataset.tvMaleR)  || 26;
    const M_SHAPE = svg.dataset.tvMaleShape || 'button';
    const MR_X = parseFloat(svg.dataset.tvMaleBodyX) || 96;
    const MR_Y = parseFloat(svg.dataset.tvMaleBodyY) || 20;
    const MR_W = parseFloat(svg.dataset.tvMaleBodyW) || 80;
    const MR_H = parseFloat(svg.dataset.tvMaleBodyH) || 80;

    let currentFemaleProgram = null;
    let currentMaleProgram = null;
    let currentColour = colourFromName('white');
    let pendingFrame = null;

    function clearGroup(g) {
      while (g && g.firstChild) g.removeChild(g.firstChild);
    }

    function renderFaceRect(linesGroup, program, bodyX, bodyY, bodyW, bodyH, inputFieldSel) {
      // Renders a rectangular face (female or rect-male) from its program +
      // live inputs.
      clearGroup(linesGroup);
      if (!program || !program.program_id) return;
      let layout = LAYOUT_CATALOG[layoutKey(program.program_id)];

      // Catalog miss → build a synthetic layout from program.line_count, same
      // pattern cattle mode uses. Large-tag programs include layouts 5/8/M
      // that aren't in LAYOUT_CATALOG (which holds Super-Maxi reference codes
      // 1/6/7/9/H/J/B/D/P/R); without this fallback the face went blank for
      // every Large+Large / Maxi+Maxi combo customer who picked one of those.
      if (!layout) {
        const liveLines = program.lines || [];
        const lineCount = program.line_count || liveLines.length;
        const yArr = FALLBACK_Y_PCT[lineCount] ||
                     FALLBACK_Y_PCT[Math.min(5, Math.max(1, lineCount))];
        if (!yArr) return;
        const defaultFontSize = Math.max(4, Math.min(
          bodyH * 0.85 / Math.max(1, lineCount),
          bodyH * 0.6
        ));
        layout = yArr.map((y_pct, idx) => ({
          line: idx + 1,
          y_pct: y_pct,
          align: 'center',
          font_size: defaultFontSize,
          max_chars: 10
        }));
      }

      const values = {};
      document.querySelectorAll(inputFieldSel).forEach(i => {
        const n = parseInt(i.dataset.line, 10);
        if (n) values[n] = (i.value || '').trim();
      });

      layout.forEach(catalogSpec => {
        const liveSpec = (program.lines || []).find(l => l.line === catalogSpec.line);
        const spec = mergeSpec(catalogSpec, liveSpec, bodyH);
        if (!spec.font_size || !spec.max_chars) return;

        const rawText = (values[spec.line] || '').toUpperCase().slice(0, spec.max_chars);
        const text = rawText || PLACEHOLDER_PER_LINE[spec.line] || '';
        const isPlaceholder = !rawText;

        const y = bodyY + (spec.y_pct * bodyH);
        const x = bodyX + (bodyW / 2);
        const maxTextWidth = bodyW - 8;

        const t = document.createElementNS(SVG_NS, 'text');
        t.setAttribute('x', x);
        t.setAttribute('y', y);
        t.setAttribute('font-size', spec.font_size);
        t.setAttribute('text-anchor', 'middle');
        t.setAttribute('fill', currentColour.text);
        if (isPlaceholder) t.setAttribute('opacity', '0.22');
        // Measured-width clamp (shared with cattle + feedlot) — short text
        // renders at natural size, only true overflow gets squished.
        if (shouldClampWidth(text, spec.font_size, maxTextWidth)) {
          t.setAttribute('textLength', maxTextWidth);
          t.setAttribute('lengthAdjust', 'spacingAndGlyphs');
        }
        t.textContent = text;
        linesGroup.appendChild(t);
      });
    }

    function renderFaceMaleButton(program, inputFieldSel) {
      // Male side is a Button (29mm circle). Button programs are typically 1-2
      // lines (per Allflex's Laza printing spec). Render up to 2 lines centred.
      clearGroup(linesMale);
      const values = {};
      document.querySelectorAll(inputFieldSel).forEach(i => {
        const n = parseInt(i.dataset.line, 10);
        if (n) values[n] = (i.value || '').trim();
      });

      const lineCount = (program && program.line_count) || 0;
      if (lineCount === 0) return;

      // Use FALLBACK_Y_PCT scaled to button geometry (M_CY ± M_R)
      const yArr = FALLBACK_Y_PCT[Math.min(2, lineCount)] || FALLBACK_Y_PCT[1];
      const fontSize = Math.max(5, Math.min((M_R * 2) * 0.45, (M_R * 2) * 0.32));
      const maxTextWidth = (M_R * 2) - 6;

      yArr.forEach((y_pct, idx) => {
        const lineNum = idx + 1;
        const liveSpec = ((program && program.lines) || []).find(l => l.line === lineNum);
        const maxChars = (liveSpec && liveSpec.max_chars) || 5;
        const rawText = (values[lineNum] || '').toUpperCase().slice(0, maxChars);
        if (!rawText) return;

        const y = (M_CY - M_R) + (y_pct * (M_R * 2));
        const t = document.createElementNS(SVG_NS, 'text');
        t.setAttribute('x', M_CX);
        t.setAttribute('y', y);
        t.setAttribute('font-size', fontSize);
        t.setAttribute('text-anchor', 'middle');
        t.setAttribute('fill', currentColour.text);
        // Measured-width clamp — button face is narrow, so 3-4 wide chars can
        // already exceed maxTextWidth at the chosen font size.
        if (shouldClampWidth(rawText, fontSize, maxTextWidth)) {
          t.setAttribute('textLength', maxTextWidth);
          t.setAttribute('lengthAdjust', 'spacingAndGlyphs');
        }
        t.textContent = rawText;
        linesMale.appendChild(t);
      });
    }

    function renderFaceMaleRectangle(program, inputFieldSel) {
      renderFaceRect(linesMale, program, MR_X, MR_Y, MR_W, MR_H, inputFieldSel);
    }

    function renderAll() {
      renderFaceRect(linesFemale, currentFemaleProgram, F_X, F_Y, F_W, F_H,
                     'input[data-allflex-field="print-line-female"]');
      if (M_SHAPE === 'rect') {
        renderFaceMaleRectangle(currentMaleProgram, 'input[data-allflex-field="print-line-male"]');
      } else {
        renderFaceMaleButton(currentMaleProgram, 'input[data-allflex-field="print-line-male"]');
      }
      if (caption) {
        const fSel = document.querySelector('[data-allflex-field="print-style-female"]');
        const mSel = document.querySelector('[data-allflex-field="print-style-male"]');
        const parts = [];
        if (fSel && fSel.value && fSel.value !== 'blank') parts.push('female ' + fSel.value);
        if (mSel && mSel.value && mSel.value !== 'blank') parts.push('male ' + mSel.value);
        caption.textContent = parts.length
          ? 'Live preview — ' + parts.join(', ')
          : 'Live preview — pick a Print Style for each tag';
      }
    }

    function applyColour() {
      if (fillFemale) fillFemale.setAttribute('fill', currentColour.fill);
      if (fillMale)   fillMale.setAttribute('fill',   currentColour.fill);
      renderAll();
    }

    function schedule() {
      if (pendingFrame) return;
      pendingFrame = requestAnimationFrame(() => { pendingFrame = null; renderAll(); });
    }

    document.addEventListener('printLinesChanged', e => {
      if (!e.detail) return;
      const side = e.detail.side;
      if (side !== 'female' && side !== 'male') return;
      // Build a synthetic program when print-line-inputs.js dispatches the
      // fallback event (no real Allflex program metaobject — fires for products
      // whose male/female print_programs aren't fully populated, including most
      // matching combo + TSU kits). Cattle mode already does this; combo mode
      // didn't, which is why the male side stayed blank no matter what the
      // customer typed. renderFaceMaleButton/Rectangle handle program.lines=[]
      // cleanly via FALLBACK_Y_PCT + per-line max_chars defaults.
      const selectEl = document.querySelector(
        '[data-allflex-field="print-style-' + side + '"]'
      );
      const selValue = selectEl ? selectEl.value : null;
      const program = e.detail.program || (e.detail.lineCount > 0 ? {
        program_id: selValue || ('lines-' + e.detail.lineCount),
        lines: [],
        line_count: e.detail.lineCount
      } : null);
      if (side === 'female') currentFemaleProgram = program;
      else                   currentMaleProgram   = program;
      schedule();
    });

    document.addEventListener('change', e => {
      const radio = e.target.closest('input[type="radio"][data-allflex-field="color"]');
      if (radio && radio.checked) {
        currentColour = colourFromName(radio.value);
        applyColour();
      }
    });

    document.addEventListener('input', e => {
      if (e.target.closest('input[data-allflex-field="print-line-female"], input[data-allflex-field="print-line-male"]')) {
        schedule();
      }
    });

    const checkedColour = document.querySelector('input[type="radio"][data-allflex-field="color"]:checked');
    if (checkedColour) currentColour = colourFromName(checkedColour.value);

    // Seed from any already-selected programs (window.printLineInputs sets these
    // on init); print-line-inputs.js then fires the printLinesChanged event on
    // initial change() so this is a defensive pre-fill.
    if (window.printLineInputs && typeof window.printLineInputs.getSelectedPrograms === 'function') {
      const sel = window.printLineInputs.getSelectedPrograms();
      if (sel) {
        if (sel.female) currentFemaleProgram = sel.female;
        if (sel.male)   currentMaleProgram   = sel.male;
      }
    }
    applyColour();
  }

  function boot() {
    document.querySelectorAll('[data-tag-visualiser]').forEach(initVisualiser);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
